import React, { useState, useEffect } from 'react';
import axiosInstance from '../../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  AlertTriangle, CheckCircle2, XCircle, TrendingUp, Users, ShoppingBag,
  Bot, Link2, Search, Smartphone, Activity, Zap, ArrowRight
} from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';

const sevMeta = {
  critical: { bg: 'bg-red-50', border: 'border-red-500', text: 'text-red-700', icon: XCircle },
  high: { bg: 'bg-orange-50', border: 'border-orange-500', text: 'text-orange-700', icon: AlertTriangle },
  medium: { bg: 'bg-amber-50', border: 'border-amber-400', text: 'text-amber-700', icon: AlertTriangle },
  low: { bg: 'bg-blue-50', border: 'border-blue-400', text: 'text-blue-700', icon: AlertTriangle },
};

export default function AdminBusinessHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get(`${API_URL}/api/admin/business-health`);
      setData(r.data);
    } catch { toast.error('Failed to load business health'); }
    finally { setLoading(false); }
  };

  const fmt = (n) => (Number(n) || 0).toLocaleString('en-IN');

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-business-health">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Business Health</h1>
          <p className="text-gray-600 mt-1">
            Deep diagnostic of why your site is or isn&apos;t converting visitors into orders (last 90 days)
          </p>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
          </div>
        ) : !data ? (
          <div className="text-center py-16 text-gray-500">Unable to load diagnostics</div>
        ) : (
          <>
            {/* Health Score */}
            <Card className={`border-l-8 ${
              data.score >= 80 ? 'border-l-emerald-500' :
              data.score >= 50 ? 'border-l-amber-500' : 'border-l-red-500'
            }`}>
              <CardContent className="p-6 flex items-center justify-between flex-wrap gap-4">
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wide">Overall Health Score</p>
                  <div className="flex items-baseline gap-3 mt-1">
                    <span className={`text-5xl font-bold ${
                      data.score >= 80 ? 'text-emerald-600' :
                      data.score >= 50 ? 'text-amber-600' : 'text-red-600'
                    }`} data-testid="health-score">{data.score}</span>
                    <span className="text-2xl text-gray-400">/ 100</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">Issues found</p>
                  <p className="text-3xl font-bold text-gray-900">{data.issues.length}</p>
                </div>
              </CardContent>
            </Card>

            {/* Key Numbers */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-blue-600"><Users className="w-4 h-4"/><span className="text-xs uppercase">Visitors</span></div>
                  <p className="text-2xl font-bold mt-1">{fmt(data.conversion.sessions_visited)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-purple-600"><ShoppingBag className="w-4 h-4"/><span className="text-xs uppercase">Reached Cart</span></div>
                  <p className="text-2xl font-bold mt-1">{fmt(data.conversion.sessions_added_to_cart)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-emerald-600"><CheckCircle2 className="w-4 h-4"/><span className="text-xs uppercase">Placed Order</span></div>
                  <p className="text-2xl font-bold mt-1">{fmt(data.conversion.sessions_placed_order)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-amber-600"><TrendingUp className="w-4 h-4"/><span className="text-xs uppercase">Conversion</span></div>
                  <p className="text-2xl font-bold mt-1">{data.conversion.conversion_pct}%</p>
                </CardContent>
              </Card>
            </div>

            {/* Issues */}
            {data.issues.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Zap className="w-5 h-5 text-amber-500" />
                    What&apos;s killing your conversions ({data.issues.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {data.issues.map((iss) => {
                    const meta = sevMeta[iss.severity] || sevMeta.medium;
                    const Icon = meta.icon;
                    return (
                      <div key={iss.code}
                        className={`p-4 rounded-lg border-l-4 ${meta.border} ${meta.bg}`}
                        data-testid={`issue-${iss.code}`}
                      >
                        <div className="flex items-start gap-3">
                          <Icon className={`w-5 h-5 mt-0.5 ${meta.text}`} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className={`font-semibold ${meta.text}`}>{iss.title}</span>
                              <Badge variant="outline" className={`${meta.text} text-xs uppercase`}>{iss.severity}</Badge>
                            </div>
                            <p className="text-sm text-gray-700 mb-2">{iss.detail}</p>
                            <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                              <ArrowRight className="w-4 h-4" /> {iss.action}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Traffic Quality */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Bot className="w-5 h-5 text-slate-600" /> Traffic Quality
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Total pageviews</p>
                    <p className="text-xl font-bold">{fmt(data.traffic.total_pageviews_90d)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Mobile %</p>
                    <p className={`text-xl font-bold ${data.traffic.mobile_pct < 20 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {data.traffic.mobile_pct}%
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Likely bots</p>
                    <p className={`text-xl font-bold ${data.traffic.likely_bot_traffic ? 'text-red-600' : 'text-emerald-600'}`}>
                      {data.traffic.likely_bot_traffic ? 'YES' : 'NO'}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 border-t pt-2">
                  Real Indian ecommerce sites see 70-85% mobile traffic. Lower than 20% mobile usually means bots or crawlers scraping your site.
                </p>
              </CardContent>
            </Card>

            {/* Dead URLs */}
            {data.traffic.dead_urls?.length > 0 && (
              <Card className="border-l-4 border-l-red-500">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Link2 className="w-5 h-5 text-red-600" />
                    Broken legacy URLs still being hit ({fmt(data.traffic.dead_url_total_hits)} times)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-3">
                    These are old Shopify-style URLs (with <code>.json</code>) still linked from Google. Every hit is a customer who couldn&apos;t find your product.
                  </p>
                  <div className="max-h-64 overflow-y-auto border rounded">
                    <table className="w-full text-sm">
                      <tbody className="divide-y">
                        {data.traffic.dead_urls.map((u) => (
                          <tr key={u.path} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-mono text-xs text-red-700 truncate max-w-md">{u.path}</td>
                            <td className="px-3 py-2 text-right font-semibold">{fmt(u.hits)} hits</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Customer errors */}
            {data.customer_errors?.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    Customer-facing errors
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {data.customer_errors.map((e) => (
                      <div key={e.reason} className="flex items-center justify-between p-2 bg-amber-50 rounded">
                        <span className="text-sm font-medium">{e.reason}</span>
                        <span className="text-sm font-bold text-amber-700">{e.count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
